import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useStore } from 'zustand';

import { useGoogleMaps } from './use-google-maps.hook';
import { useRecentLocationSearches } from './use-recent-location-searches.hook';

import { AutocompletePrediction, Place } from '@/types/google-maps.types';
import { getCitySlug } from '@/app/(main)/(pages)/_services/get-city-slug.service';
import { useSearchParams } from '@/hooks/use-search-params.hook';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';
import { slugify } from '@/utils/formatters/slugfy';
import { setUserLocation } from '@/store/user-location.store';
import { setOpenLocationSelectionModal, setDeniedGeolocation, modalsOpeningStore } from '@/store/modal';
import { openSearchModal } from '@/store/search/search.store';
import { branchesListId } from '@/app/(main)/(pages)/_components/branches-section/consts/branches-section.const';
import { getLogger } from '@/logger/logger';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { buildUrlWithPersistedParams } from '@/utils/persisted-params.util';

const MODAL_CLOSE_DELAY = 100;

export function useLocationSelection() {
  const router = useRouter();
  const searchParams = useSearchParams([...Object.values(PersistedUserNavigationSearchParams)]);
  const analytics = getAnalyticsPlugin();
  const logger = getLogger('LocationSelectionModal');

  const {
    getGoogleMapsPredictions,
    getGoogleMapsPlaceDetailsWithGeocoder,
    getGoogleMapsPlaceByCoordinates,
  } = useGoogleMaps();

  const { addRecentSearch } = useRecentLocationSearches();

  const {
    locationCallback,
    pendingSearchTerm,
    shouldOpenSearchModal,
    shouldScrollToBranchesListOnNavigate,
  } = useStore(modalsOpeningStore, (state) => ({
    locationCallback: state?.locationCallback,
    pendingSearchTerm: state?.pendingSearchTerm,
    shouldOpenSearchModal: state?.shouldOpenSearchModal,
    shouldScrollToBranchesListOnNavigate: state?.shouldScrollToBranchesListOnNavigate,
  }));

  const navigateToCityPage = useCallback(async (place: Place): Promise<void> => {
    const city = await getCitySlug(place.city, place.state);

    if (!city?.slug) {
      return;
    }

    const baseUrl = `/cidades/${city.slug}`;
    const link = buildUrlWithPersistedParams({
      baseUrl,
      overrides: {
        [PersistedUserNavigationSearchParams.CIDADE]: city.slug,
      },
    });

    const finalLink = shouldScrollToBranchesListOnNavigate
      ? `${link}#${branchesListId}`
      : link;

    await router.push(finalLink);
  }, [router, shouldScrollToBranchesListOnNavigate]);

  const updateUserLocation = useCallback((place: Place) => {
    setUserLocation(place);
    searchParams.setParams({
      [PersistedUserNavigationSearchParams.CIDADE]: slugify(
        `${place.city}-${place.state}`,
      ),
    });
  }, [searchParams]);

  const handlePostLocationActions = useCallback(async (place: Place) => {
    setOpenLocationSelectionModal(false);

    if (locationCallback) {
      await new Promise((resolve) => setTimeout(resolve, MODAL_CLOSE_DELAY));
      locationCallback(place);
      if (pendingSearchTerm !== undefined) {
        openSearchModal(true, pendingSearchTerm);
      }
    } else {
      await navigateToCityPage(place);
      if (pendingSearchTerm !== undefined) {
        openSearchModal(true, pendingSearchTerm);
      } else if (shouldOpenSearchModal) {
        openSearchModal(true);
      }
    }
  }, [locationCallback, pendingSearchTerm, shouldOpenSearchModal, navigateToCityPage]);

  const selectLocation = useCallback(async (
    place: Place,
    prediction?: AutocompletePrediction,
    analyticsEvent?: typeof AnalyticsEnum[keyof typeof AnalyticsEnum],
  ) => {
    if (analyticsEvent) {
      analytics.track(analyticsEvent, {
        cidade: prediction?.description || place.city,
      });
    }

    updateUserLocation(place);

    if (prediction) {
      addRecentSearch(prediction);
    }

    await handlePostLocationActions(place);
  }, [analytics, updateUserLocation, addRecentSearch, handlePostLocationActions]);

  const handleMyLocation = useCallback(async (): Promise<void> => {
    analytics.track(AnalyticsEnum.USE_MY_LOCATION);

    try {
      const location = await navigator.permissions.query({
        name: 'geolocation',
      });

      if (location.state === 'denied') {
        setDeniedGeolocation(true);
        return;
      }

      const place = await getGoogleMapsPlaceByCoordinates();
      await selectLocation(place);
    } catch (error) {
      logger.error('Error using geolocation', { error });
      setDeniedGeolocation(true);
    }
  }, [analytics, getGoogleMapsPlaceByCoordinates, selectLocation, logger]);

  const handleSearchResultClick = useCallback(async (
    prediction: AutocompletePrediction,
  ) => {
    let predictions = [] as AutocompletePrediction[];

    if (!prediction.placeId) {
      predictions = await getGoogleMapsPredictions(prediction.description);
    }

    const placeId = predictions.length
      ? predictions[0].placeId
      : prediction.placeId;

    const place = await getGoogleMapsPlaceDetailsWithGeocoder(placeId);

    await selectLocation(
      place,
      prediction,
      AnalyticsEnum.CLICK_SEARCH_RESULT_CITY,
    );
  }, [getGoogleMapsPredictions, getGoogleMapsPlaceDetailsWithGeocoder, selectLocation]);

  const handleSuggestionClick = useCallback(async (place: Place) => {
    const prediction: AutocompletePrediction = {
      placeId: place.placeId,
      description: place.formattedAddress
        || `${place.city}${place.state ? `, ${place.state}` : ''}`,
      formattedAddress: place.formattedAddress,
      latitude: place.latitude,
      longitude: place.longitude,
      types: place.types,
      city: place.city,
      state: place.state,
      street: place.street,
      locationType: place.locationType,
    };

    await selectLocation(
      place,
      prediction,
      AnalyticsEnum.CLICK_MOST_SEARCHED_CITY,
    );
  }, [selectLocation]);

  return {
    handleMyLocation,
    handleSearchResultClick,
    handleSuggestionClick,
  };
}
